import { computed, onBeforeUnmount, ref, watch, type ComputedRef } from 'vue';
import { useConvexMutation, useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { getConvexSiteUrl, readStoredToken } from './auth';

const HEARTBEAT_INTERVAL_MS = 5_000;
const ACTIVITY_CLOCK_INTERVAL_MS = 500;
const ACTIVE_DRAWING_WINDOW_MS = 1_500;
const ACTIVE_TYPING_WINDOW_MS = 5_000;

type TrackState = {
  audio: boolean;
  camera: boolean;
  screenshare: boolean;
};

type PermissionSet = {
  audio: boolean;
  chat: boolean;
  navigate: boolean;
  rooms: boolean;
  screenshare: boolean;
  topMenuControl: boolean;
  video: boolean;
  whiteboard: boolean;
};

export type ConferenceRole = 'student' | 'teacher' | 'recorder';

export type ConferenceParticipant = {
  apparentRoom: {
    displayName: string;
    slug: string;
  };
  displayName: string;
  hand: boolean;
  handRaisedAt: number;
  id: Id<'participants'>;
  isKicked: boolean;
  kickReleaseAt: number | null;
  isViewer: boolean;
  participationCount: number;
  permissions: PermissionSet;
  role: ConferenceRole;
  trackState: TrackState;
};

export type ConferenceRosterParticipant = {
  displayName: string;
  emoji: string;
  feedback: string;
  hand: boolean;
  handRaisedAt: number;
  id: Id<'participants'>;
  isDrawing: boolean;
  isTyping: boolean;
  isViewer: boolean;
  participationCount: number;
  permissions: PermissionSet;
  role: ConferenceRole;
  status: string;
  trackState: TrackState;
};

export type ConferenceRoom = {
  displayName: string;
  funnelChat: boolean;
  isActive: boolean;
  isOpen: boolean;
  kind: 'breakout' | 'main';
  occupancyCount: number;
  slug: string;
};

export type ConferenceRoomActivity = {
  drawingParticipantIds: ConferenceParticipant['id'][];
  typingParticipantIds: ConferenceParticipant['id'][];
};

type ConferenceRoomActivityEntry = {
  participantId: ConferenceParticipant['id'];
  updatedAt: number;
};

export type ConferenceLogEntry = {
  createdAt: number;
  detail?: string;
  id: string;
  label: string;
};

export type ConferenceState = {
  conference: {
    chatEnabled: boolean;
    feedbackVisibility: boolean;
    permissions: PermissionSet;
    slug: string;
    timerExpirationDate: number;
    title: string;
  };
  currentRoom: {
    displayName: string;
    funnelChat: boolean;
    slug: string;
  };
  participants: ConferenceParticipant[];
  participantsByRoomSlug: Record<string, ConferenceRosterParticipant[]>;
  roomActivityByRoomSlug: Record<string, ConferenceRoomActivity>;
  rooms: ConferenceRoom[];
  viewer: {
    apparentRoom: {
      displayName: string;
      slug: string;
    };
    cloakedRoomName: string | null;
    displayName: string;
    emoji: string;
    feedback: string;
    hand: boolean;
    hasPersistedPreferences: boolean;
    id: Id<'participants'>;
    isKicked: boolean;
    kickReleaseAt: number | null;
    permissions: PermissionSet;
    preferences: {
      accessibility: {
        textSize: 'default' | 'large' | 'largest';
      };
      media: {
        selectedAudioInput: string;
        selectedAudioOutput: string;
        selectedCamera: string;
      };
      notifications: {
        joinedSessionDing: boolean;
        leftSessionDing: boolean;
        myLowerHandDing: boolean;
        myRaiseHandDing: boolean;
        otherRaiseHandDing: boolean;
        privateChatReceivedDing: boolean;
      };
    };
    role: ConferenceRole;
    status: string;
    trackState: TrackState;
  };
};

type ConferenceShellState = Omit<ConferenceState, 'participants' | 'participantsByRoomSlug' | 'roomActivityByRoomSlug' | 'viewer'> & {
  rooms: Array<Omit<ConferenceRoom, 'occupancyCount'>>;
};

type ConferenceRosterState = {
  occupancyByRoomSlug: Record<string, number>;
  participants: ConferenceParticipant[];
  participantsByRoomSlug: Record<string, ConferenceRosterParticipant[]>;
};

type RawConferenceRoomActivity = ConferenceRoomActivity & {
  drawingActivities?: ConferenceRoomActivityEntry[];
  typingActivities?: ConferenceRoomActivityEntry[];
};

type ConferenceActivityState = {
  activityByRoomSlug: Record<string, RawConferenceRoomActivity>;
};

function resolveActiveActivityParticipantIds(
  activities: ConferenceRoomActivityEntry[] | undefined,
  fallbackParticipantIds: ConferenceParticipant['id'][] | undefined,
  now: number,
  activeWindowMs: number,
): ConferenceParticipant['id'][] {
  if (!activities) {
    return fallbackParticipantIds ?? [];
  }

  return activities
    .filter((activity) => now - activity.updatedAt <= activeWindowMs)
    .map((activity) => activity.participantId);
}

function resolveActiveRoomActivity(
  activityByRoomSlug: Record<string, RawConferenceRoomActivity>,
  now: number,
): Record<string, ConferenceRoomActivity> {
  return Object.fromEntries(
    Object.entries(activityByRoomSlug).map(([roomSlug, activity]) => [
      roomSlug,
      {
        drawingParticipantIds: resolveActiveActivityParticipantIds(
          activity.drawingActivities,
          activity.drawingParticipantIds,
          now,
          ACTIVE_DRAWING_WINDOW_MS,
        ),
        typingParticipantIds: resolveActiveActivityParticipantIds(
          activity.typingActivities,
          activity.typingParticipantIds,
          now,
          ACTIVE_TYPING_WINDOW_MS,
        ),
      },
    ]),
  );
}

export function useConference(conferenceSlug: ComputedRef<string>) {
  const bootstrapError = ref<string | null>(null);
  const interactionError = ref<string | null>(null);
  const activityNow = ref(Date.now());
  let heartbeatHandle: number | null = null;
  let activityClockHandle: number | null = null;
  let boundConferenceSlug: string | null = null;

  const queryArgs = computed(() => ({
    conferenceSlug: conferenceSlug.value,
  }));

  const { mutate: ensureConference, isPending: isEnsuringConference } = useConvexMutation(api.conferences.ensureConference);
  const { mutate: heartbeat } = useConvexMutation(api.presence.heartbeat);
  const { mutate: switchRoomMutation, isPending: isSwitchingRoom } = useConvexMutation(api.participants.switchRoom);
  const {
    data: shellData,
    error: shellError,
    isPending: isShellPending,
  } = useConvexQuery(api.conferences.getShell, queryArgs);
  const {
    data: viewerData,
    error: viewerError,
    isPending: isViewerPending,
  } = useConvexQuery(api.participants.getViewerState, queryArgs);
  const {
    data: rosterStateData,
    error: rosterStateError,
    isPending: isRosterStatePending,
  } = useConvexQuery(api.participants.getRosterState, queryArgs);
  const { data: roomActivityStateData, error: roomActivityStateError } = useConvexQuery(
    api.participants.getRoomActivityState,
    queryArgs,
  );

  async function sendHeartbeat(nextConferenceSlug: string): Promise<void> {
    await heartbeat({
      conferenceSlug: nextConferenceSlug,
      userAgent: navigator.userAgent,
    });
  }

  function stopHeartbeat(): void {
    if (heartbeatHandle !== null) {
      window.clearInterval(heartbeatHandle);
      heartbeatHandle = null;
    }
  }

  function startHeartbeat(nextConferenceSlug: string): void {
    stopHeartbeat();
    boundConferenceSlug = nextConferenceSlug;

    void sendHeartbeat(nextConferenceSlug);
    heartbeatHandle = window.setInterval(() => {
      void sendHeartbeat(nextConferenceSlug);
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stopActivityClock(): void {
    if (activityClockHandle !== null) {
      window.clearInterval(activityClockHandle);
      activityClockHandle = null;
    }
  }

  function startActivityClock(): void {
    if (activityClockHandle !== null || typeof window === 'undefined') {
      return;
    }

    activityClockHandle = window.setInterval(() => {
      activityNow.value = Date.now();
    }, ACTIVITY_CLOCK_INTERVAL_MS);
  }

  function sendDisconnectBeacon(nextConferenceSlug: string): void {
    const inviteToken = readStoredToken();

    if (!inviteToken) {
      return;
    }

    const body = new FormData();
    body.set('conferenceSlug', nextConferenceSlug);
    body.set('token', inviteToken);

    const disconnectUrl = `${getConvexSiteUrl()}/presence/disconnect`;

    if (navigator.sendBeacon) {
      navigator.sendBeacon(disconnectUrl, body);
      return;
    }

    void fetch(disconnectUrl, {
      body,
      keepalive: true,
      method: 'POST',
    });
  }

  const conference = computed<ConferenceState | null>(() => {
    const shell = (shellData.value as ConferenceShellState | null | undefined) ?? null;
    const viewer = (viewerData.value as ConferenceState['viewer'] | null | undefined) ?? null;
    const rosterState = (rosterStateData.value as ConferenceRosterState | null | undefined) ?? null;
    const roomActivityState = (roomActivityStateData.value as ConferenceActivityState | null | undefined) ?? null;
    const activeRoomActivityByRoomSlug = resolveActiveRoomActivity(
      roomActivityState?.activityByRoomSlug ?? {},
      activityNow.value,
    );

    if (!shell || !viewer || !rosterState) {
      return null;
    }

    return {
      ...shell,
      participants: rosterState.participants,
      participantsByRoomSlug: rosterState.participantsByRoomSlug,
      roomActivityByRoomSlug: activeRoomActivityByRoomSlug,
      rooms: shell.rooms.map((room) => ({
        ...room,
        occupancyCount: rosterState.occupancyByRoomSlug[room.slug] ?? 0,
      })),
      viewer,
    };
  });
  const activeRoom = computed(() => conference.value?.rooms.find((room) => room.isActive) ?? null);
  const resolvedError = computed(
    () =>
      bootstrapError.value ??
      interactionError.value ??
      shellError.value?.message ??
      viewerError.value?.message ??
      rosterStateError.value?.message ??
      roomActivityStateError.value?.message ??
      null,
  );
  const isLoading = computed(
    () =>
      isEnsuringConference.value || isShellPending.value || isViewerPending.value || isRosterStatePending.value,
  );

  watch(
    conferenceSlug,
    async (nextConferenceSlug) => {
      bootstrapError.value = null;
      interactionError.value = null;

      try {
        await ensureConference({ conferenceSlug: nextConferenceSlug });
        startHeartbeat(nextConferenceSlug);
      } catch (error) {
        bootstrapError.value = error instanceof Error ? error.message : 'Unable to load the conference.';
      }
    },
    { immediate: true },
  );

  watch(
    () => conference.value,
    (nextConference) => {
      if (!nextConference) {
        stopHeartbeat();
        return;
      }

      if (boundConferenceSlug !== nextConference.conference.slug) {
        startHeartbeat(nextConference.conference.slug);
      }
    },
  );

  const handlePageHide = () => {
    if (boundConferenceSlug) {
      sendDisconnectBeacon(boundConferenceSlug);
    }
  };

  if (typeof window !== 'undefined') {
    startActivityClock();
    window.addEventListener('pagehide', handlePageHide);
  }

  onBeforeUnmount(() => {
    stopHeartbeat();
    stopActivityClock();

    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', handlePageHide);
    }
  });

  async function switchRoom(roomSlug: string): Promise<void> {
    interactionError.value = null;

    try {
      await switchRoomMutation({
        conferenceSlug: conferenceSlug.value,
        roomSlug,
      });
    } catch (error) {
      interactionError.value = error instanceof Error ? error.message : 'Unable to switch rooms right now.';
    }
  }

  return {
    activeRoom,
    conference,
    isLoading,
    isSwitchingRoom,
    resolvedError,
    switchRoom,
  };
}
